"use client"

import {
  MatchQualityBadge,
  PersonListCard,
  briefCandidateToPersonModel,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { useCallback } from 'react'
import { toast } from 'sonner'

import {
  BRIEF_BOARD_COLUMNS,
  type BriefBoardColumn,
  type BriefCandidate,
  briefCandidateBoardColumn,
  canMoveBriefCandidate,
  useMoveBriefCandidate,
} from '../../hooks/use-briefs'
import { SimpleKanban, type SimpleKanbanCardProps } from '../employer/simple-kanban'
import { ScorecardSummaryLine } from '../interview/scorecard-summary'
import { TalentProfilePreviewCanvas } from './talent-profile-preview-canvas'

const COLUMN_SUBTITLES: Partial<Record<BriefBoardColumn, string>> = {
  interview: 'with interviewers',
  hired: 'recruiter confirmed',
}

const BOARD_COLUMNS = BRIEF_BOARD_COLUMNS.map((column) => ({
  id: column.id,
  label: column.label,
  subtitle: COLUMN_SUBTITLES[column.id],
}))

const getId = (candidate: BriefCandidate) => candidate.profile_id

export type BriefCandidatesBoardProps = {
  briefId: string
  candidates: BriefCandidate[]
}

/** Saved candidates for one brief, by where they are in the hire. */
export function BriefCandidatesBoard({ briefId, candidates }: BriefCandidatesBoardProps) {
  const { moveCandidate } = useMoveBriefCandidate()
  const { openCanvas, closeCanvas } = useArtifactCanvas()

  const openPreview = useCallback(
    (candidate: BriefCandidate) => {
      const name = briefCandidateToPersonModel(candidate).name
      openCanvas({
        title: name,
        preventAutoFocus: true,
        content: (
          <TalentProfilePreviewCanvas
            key={`brief-board-${candidate.profile_id}`}
            profileId={candidate.profile_id}
            profileName={name}
            onClose={closeCanvas}
          />
        ),
      })
    },
    [closeCanvas, openCanvas],
  )

  const handleMove = useCallback(
    (candidate: BriefCandidate, column: string) => {
      const label = BRIEF_BOARD_COLUMNS.find((c) => c.id === column)?.label ?? column
      moveCandidate(
        { briefId, profileId: candidate.profile_id, column: column as BriefBoardColumn },
        {
          onSuccess: () => toast.success(`${candidate.candidate_name} moved to ${label}`),
          onError: (error: Error) => toast.error(error.message),
        },
      )
    },
    [briefId, moveCandidate],
  )

  const renderCard = useCallback(
    (candidate: BriefCandidate, { dragHandleProps, isOverlay }: SimpleKanbanCardProps) => {
      const notes = candidate.notes?.trim()
      return (
        <PersonListCard
          variant="pipeline"
          className="cursor-pointer"
          person={briefCandidateToPersonModel(candidate)}
          onOpen={() => openPreview(candidate)}
          openAriaLabel={`Open ${candidate.candidate_name}`}
          isOverlay={isOverlay}
          surfaceProps={dragHandleProps as Record<string, unknown> | undefined}
          headerAside={
            typeof candidate.match_score === 'number' ? (
              <MatchQualityBadge score={candidate.match_score} className="text-xs" />
            ) : null
          }
          body={
            notes || candidate.analysis_headline || candidate.scorecard ? (
              <div className="space-y-1.5">
                {notes || candidate.analysis_headline ? (
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {notes || candidate.analysis_headline}
                  </p>
                ) : null}
                {candidate.scorecard ? (
                  <ScorecardSummaryLine
                    recommendation={candidate.scorecard.recommendation}
                    recommendationLabel={candidate.scorecard.recommendation_label}
                    interviewerName={candidate.scorecard.interviewer_name}
                    metTarget={candidate.scorecard.met_target}
                    rated={candidate.scorecard.rated}
                  />
                ) : null}
              </div>
            ) : null
          }
        />
      )
    },
    [openPreview],
  )

  return (
    <SimpleKanban
      ariaLabel="Brief candidates board"
      columns={BOARD_COLUMNS}
      items={candidates}
      getId={getId}
      getColumn={briefCandidateBoardColumn}
      canDrop={(candidate, column) =>
        canMoveBriefCandidate(briefCandidateBoardColumn(candidate), column as BriefBoardColumn)
      }
      onMove={handleMove}
      renderCard={renderCard}
      emptyLabel="No one here yet"
    />
  )
}
